import type { GattServer } from '@/ble/types';
import { subscribe } from '@/ble/gatt';
import { UUID } from '@/ble/uuids';
import { BleDeviceBase } from '../bleTrainerBase';
import { CrankCadence, parseCyclingPower } from './protocol';

/** Read-only Cycling Power sensor (power meter, or a trainer's CPS for cadence fallback). */
export class CpsSensor extends BleDeviceBase {
  readonly sensorKind = 'power' as const;
  private crank = new CrankCadence();
  private decayTimer: ReturnType<typeof setInterval> | null = null;

  protected async setup(server: GattServer): Promise<void> {
    const svc = await server.getPrimaryService(UUID.CPS);
    const meas = await svc.getCharacteristic(UUID.CPS_MEASUREMENT);
    this.crank.reset();
    this.unsubs.push(await subscribe(meas, (dv) => this.onMeasurement(dv)));
    this.decayTimer = setInterval(() => {
      const c = this.crank.current(Date.now());
      if (c === 0 && (this.last.cadenceRpm ?? 0) > 0) this.emitData({ cadenceRpm: 0 });
    }, 1000);
  }

  protected onTransportLost(): void {
    if (this.decayTimer) clearInterval(this.decayTimer);
    this.decayTimer = null;
  }

  private onMeasurement(dv: DataView): void {
    const m = parseCyclingPower(dv);
    const now = Date.now();
    const cadence =
      m.crankRevs !== undefined && m.lastCrankEventTime !== undefined
        ? this.crank.update(m.crankRevs, m.lastCrankEventTime, now)
        : undefined;
    this.emitData(
      cadence !== undefined
        ? { powerW: Math.max(0, m.powerW), cadenceRpm: cadence }
        : { powerW: Math.max(0, m.powerW) },
    );
  }
}
